import React, { Component } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { noop } from 'lodash';
import { Modal, ModalContent } from '@zocdoc-frontend-common/components-modal';
import { blue100, white } from '@zocdoc-mezzanine/foundations-colors';
import { FontFamily } from '@zocdoc-frontend-common/styles-typography';
import { Button, VARIATION } from '@zocdoc-frontend-common/components-button';
import MagnifyingGlass from '@zocdoc-frontend-common/assets-icons/MagnifyingGlass';
import { breakpoint } from '@zocdoc-frontend-common/styles-grid';
import MentalHealthOONModal from '@zocdoc-frontend-common/components-mental-health-insurance-oon-modal';
import {
    OUT_OF_NETWORK_STRINGS,
    IN_NETWORK_BOOKABLE_ONLY_STRINGS,
} from './constants';
import { getSearchUrl } from '../../utils/getSearchUrl';

export class OutOfNetworkModalView extends Component {
    static propTypes = {
        insuranceCarrierId: PropTypes.string.isRequired,
        insuranceName: PropTypes.string.isRequired,
        insurancePlanId: PropTypes.string.isRequired,
        procedureId: PropTypes.string.isRequired,
        professionalName: PropTypes.string.isRequired,
        shouldShowMentalHealthOONBenefits: PropTypes.bool,
        trackClickCloseModal: PropTypes.func,
        trackClickContinueBookingButton: PropTypes.func,
        trackClickFindDoctorsButton: PropTypes.func,
        whiteLabelVanityUrl: PropTypes.string,
        sendMetric: PropTypes.func,
        isOnlyInNetworkBookable: PropTypes.bool,
    };

    static defaultProps = {
        trackClickCloseModal: noop,
        trackClickContinueBookingButton: noop,
        trackClickFindDoctorsButton: noop,
    };

    render() {
        const {
            insuranceCarrierId,
            insuranceCarrierName,
            insuranceName,
            insurancePlanName,
            insurancePlanId,
            shouldShowMentalHealthOONBenefits,
            procedureId,
            professional,
            professionalName,
            whiteLabelVanityUrl,
            sendMetric,
            isOnlyInNetworkBookable,
        } = this.props;

        const searchUrl = getSearchUrl(
            procedureId,
            professional.specialtyId,
            insuranceCarrierId,
            insurancePlanId,
            whiteLabelVanityUrl
        );

        const header = isOnlyInNetworkBookable
            ? professionalName +
              IN_NETWORK_BOOKABLE_ONLY_STRINGS.OutOfNetworkHeader
            : OUT_OF_NETWORK_STRINGS.OutOfNetworkHeader;

        return shouldShowMentalHealthOONBenefits && !isOnlyInNetworkBookable ? (
            <MentalHealthOONModal
                data-test="mh-oon-modal"
                ignoreClickOutside
                onClickCancel={this.handleClickCloseModal}
                onClickContinue={this.handleClickContinueButton}
                beforeClickFindInNetworkDoctors={
                    this.handleClickFindDoctorsButton
                }
                findDoctorsUrl={searchUrl}
                providerName={professional.nameInSentence}
                providerPictureUrl={professional.frontEndCirclePictureUrl}
                insuranceName={`${insuranceCarrierName} ${insurancePlanName}`}
                selfPaySettings={professional.selfPaySettings}
                sendMetric={sendMetric}
            />
        ) : (
            <OutOfNetworkModal
                data-test="out-of-network-find-doctor-modal"
                ignoreClickOutside
                onClickCancel={this.handleClickCloseModal}
            >
                <OutOfNetworkHeader data-test="out-of-network-modal-header">
                    {header}
                </OutOfNetworkHeader>
                {isOnlyInNetworkBookable ? (
                    <OutOfNetworkMessage data-test="in-network-only-modal-message">
                        {IN_NETWORK_BOOKABLE_ONLY_STRINGS.OutOfNetworkMessage}
                    </OutOfNetworkMessage>
                ) : (
                    <OutOfNetworkMessage data-test="out-of-network-modal-message">
                        {`${professionalName} is out-of-network for the insurance you selected `}
                        <InsuranceInfo>{insuranceName}</InsuranceInfo>. See more
                        doctors that take your carrier and plan with similar
                        availabilities.
                    </OutOfNetworkMessage>
                )}
                {isOnlyInNetworkBookable ? (
                    <FindDoctorsButtonInNetworkOnly
                        beforeClick={this.handleClickFindDoctorsButton}
                        data-test="out-of-network-modal-find-doctor-button"
                        href={searchUrl}
                        size={Button.SIZE.SMALL}
                        variation={VARIATION.PRIMARY}
                    >
                        <StyledMagnifyingGlass />
                        {IN_NETWORK_BOOKABLE_ONLY_STRINGS.FindDoctorsButton}
                    </FindDoctorsButtonInNetworkOnly>
                ) : (
                    <OutOfNetworkButtons>
                        <ContinueBookingButton
                            data-test="out-of-network-modal-continue-booking-button"
                            onClick={this.handleClickContinueButton}
                            size={Button.SIZE.SMALL}
                            variation={VARIATION.PRIMARY}
                        >
                            {OUT_OF_NETWORK_STRINGS.ContinueBookingButton}
                        </ContinueBookingButton>
                        <FindDoctorsButton
                            beforeClick={this.handleClickFindDoctorsButton}
                            data-test="out-of-network-modal-find-doctor-button"
                            href={searchUrl}
                            size={Button.SIZE.SMALL}
                            variation={VARIATION.SECONDARY}
                        >
                            <StyledMagnifyingGlass />
                            {OUT_OF_NETWORK_STRINGS.FindDoctorsButton}
                        </FindDoctorsButton>
                    </OutOfNetworkButtons>
                )}
            </OutOfNetworkModal>
        );
    }

    handleClickFindDoctorsButton = () => {
        const {
            procedureId,
            trackClickFindDoctorsButton,
            shouldShowMentalHealthOONBenefits,
            isOnlyInNetworkBookable,
        } = this.props;

        trackClickFindDoctorsButton(procedureId, {
            is_mh_test_variant: shouldShowMentalHealthOONBenefits,
            is_only_in_network_bookable: isOnlyInNetworkBookable,
        });
    };

    handleClickCloseModal = () => {
        const {
            insurancePlanId,
            trackClickCloseModal,
            shouldShowMentalHealthOONBenefits,
            isOnlyInNetworkBookable,
        } = this.props;

        trackClickCloseModal({
            insurance_plan_id: insurancePlanId,
            is_mh_test_variant: shouldShowMentalHealthOONBenefits,
            is_only_in_network_bookable: isOnlyInNetworkBookable,
        });
    };

    handleClickContinueButton = () => {
        const {
            trackClickContinueBookingButton,
            shouldShowMentalHealthOONBenefits,
        } = this.props;

        trackClickContinueBookingButton({
            is_mh_test_variant: shouldShowMentalHealthOONBenefits,
        });
    };
}

const OutOfNetworkModal = styled(Modal)`
    ${ModalContent} {
        display: flex;
        flex-direction: column;
        padding: 35px;
        padding-bottom: 30px;
        width: 600px;

        ${breakpoint('xsmall', { down: true })} {
            padding: 35px 15px;
            padding-bottom: 20px;
            width: 100%;
        }
    }
`;

const OutOfNetworkHeader = styled.div`
    color: ${blue100};
    font: 22px/21px ${FontFamily.SharpSansBold};
`;

const OutOfNetworkMessage = styled.div`
    color: ${blue100};
    font: 15px/22px ${FontFamily.SharpSansMedium};
    padding-top: 12px;
`;

const InsuranceInfo = styled.span`
    color: ${blue100};
    font: 15px/22px ${FontFamily.SharpSansBold};
`;

const OutOfNetworkButtons = styled.div`
    display: flex;
    flex-direction: row;
    padding-top: 25px;

    ${breakpoint('xsmall', { down: true })} {
        flex-direction: column;
    }
`;

const ContinueBookingButton = styled(Button)`
    flex: 1;
    font: 16px/28px ${FontFamily.SharpSansSemibold};
    min-height: 48px;
`;

const FindDoctorsButton = styled(ContinueBookingButton)`
    margin-left: 20px;

    ${breakpoint('xsmall', { down: true })} {
        margin-left: 0;
        margin-top: 10px;
    }

    &:hover {
        path {
            fill: ${white};
        }
    }
`;

const FindDoctorsButtonInNetworkOnly = styled(Button)`
    width: 100%;
    margin-top: 25px;
`;

const StyledMagnifyingGlass = styled(MagnifyingGlass)`
    height: 16px;
    padding-right: 6px;
    padding-top: 4px;
    width: 16px;
`;
